/**
 * Suspicious v2 §3.2 — redesigned detail panel for one sender group.
 *
 * Header (serif 22 "{a} and {b}" / "{a}, {b} and {n} more") + mono
 * subline "{n} requests · first seen 15h ago · last 2h ago".
 * Then BlockControl + status line directly below the header. AI SUMMARY
 * card (only when present) with a verdict chip; footnote "Written by AI
 * from the requests below. Advice only." renders exactly once.
 *
 * WHO (omit unknown rows). WHAT THEY TRIED (summary line + merged
 * requests newest-first under day headers). WHY IT'S FLAGGED (confidence
 * moves into the kicker). HISTORY (On this site / Before this /
 * Other sites).
 *
 * No banned strings: Observe, Enforce, Watch only, Firewall mode,
 * Suggested action, Verdict. The verdict chip label text uses "Looks
 * safe" / "Suggests watching" / "Suggests blocking" per spec.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { Card, ConfirmDialog, RelativeTime, Tooltip } from '../../components/atoms/index.js';
import { CountryFlag, countryName } from '../../components/atoms/CountryFlag.js';
import { useOptionalHttp } from '../../api/http-context.js';
import type { HttpClient } from '../../api/http-client.js';
import { getBlocking, getThreatSender, postManualBlock, postSenderIgnore } from '../../api/threats.js';
import { TurnOnBlockingLink } from './TurnOnBlocking.js';
import { useDisplayTimeZone } from '../../app/time-zone-context.js';
import { useC } from '../../lib/theme.js';
import type {
  BlockState,
  BlockingState,
  ManualBlockDuration,
  ThreatSenderDetailResponse,
  ThreatSenderSummary,
} from '@sill/types/api';

import { blockUnavailableReason, type BlockControlOutcome, useIsShopifySite } from './BlockControl.js';
import { DetailActionRow, type ActionDuration } from './DetailActionRow.js';
import { severityIcon } from './severity-icon.js';
import { useIsMobile } from '../../lib/use-is-mobile.js';
import { plainClassLine } from '../security/attack-class-plain.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

/**
 * Founder bug 2026-10-18 (block-state consistency): same chip shape
 * as the Person / Bot / Scanner rows. "Blocked until <date>" /
 * "Blocked forever" / "Block saved · not active" when the site
 * isn't delivering (mode=off / delivery=failing / install not Worker).
 */
function senderChipLabel(
  block: NonNullable<BlockState['block']>,
  blocking: BlockState['blocking'],
): string {
  const notDelivering = blocking.mode === 'off' || blocking.delivery === 'failing' || blocking.install !== 'worker_ready';
  if (notDelivering) return 'Block saved · not active';
  if (block.expires_at === null) return 'Blocked forever';
  try {
    const d = new Date(block.expires_at);
    if (Number.isNaN(d.getTime())) return 'Blocked';
    return `Blocked until ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d)}`;
  } catch { return 'Blocked'; }
}

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'loaded'; readonly data: ThreatSenderDetailResponse };

export interface SenderDetailProps {
  readonly siteId: string;
  readonly siteDomain: string | null;
  readonly sender: ThreatSenderSummary;
  /** Fires after a block / unblock / trust / ignore lands, so the list can refresh. */
  readonly onChanged?: () => void;
}

export function SenderDetail({ siteId, siteDomain, sender, onChanged }: SenderDetailProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const tz = useDisplayTimeZone();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (http === null) {
      setState({ kind: 'error', message: 'The dashboard is offline.' });
      return;
    }
    setState({ kind: 'loading' });
    let live = true;
    getThreatSender(http, siteId, sender.sender_ref)
      .then((data) => {
        if (!live) return;
        if (data === null) setState({ kind: 'error', message: 'This alert is no longer visible.' });
        else setState({ kind: 'loaded', data });
      })
      .catch(() => {
        if (live) setState({ kind: 'error', message: 'The alert details could not be loaded.' });
      });
    return () => { live = false; };
  }, [http, siteId, sender.sender_ref]);

  return (
    <Card data-testid="sender-detail" style={{ padding: 20 }}>
      {state.kind === 'loading' ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Loading the full event&hellip;</div>
      ) : state.kind === 'error' ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>{state.message}</div>
      ) : (
        <SenderBody
          data={state.data}
          siteId={siteId}
          siteDomain={siteDomain}
          tz={tz}
          http={http}
          onActionsChange={(next_actions) => {
            setState((prev) => (prev.kind === 'loaded'
              ? { kind: 'loaded', data: { ...prev.data, actions: next_actions } }
              : prev
            ));
            onChanged?.();
          }}
        />
      )}
    </Card>
  );
}

function SenderBody({
  data,
  siteId,
  siteDomain,
  tz,
  http,
  onActionsChange,
}: {
  readonly data: ThreatSenderDetailResponse;
  readonly siteId: string;
  readonly siteDomain: string | null;
  readonly tz: string;
  readonly http: HttpClient | null;
  readonly onActionsChange: (next: BlockState) => void;
}): JSX.Element {
  const C = useC();
  const isShopify = useIsShopifySite(siteId);
  const sev = severityIcon(data.sender.max_severity, C);
  const titleParts = buildTitleParts(data.sender.class_labels);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  // Founder bug 2026-10-18: Trust needs a confirm dialog so the
  // merchant knows it (a) removes an existing block, (b) adds an
  // allowlist entry. One shared confirm; sender detail owns it.
  const [trustConfirming, setTrustConfirming] = useState(false);
  // Founder bug 2026-10-20 (coordinator item 8): the right panel used
  // to show the server's `self_block_confirm_required` message as a
  // plain muted sentence with no buttons, so the merchant could not
  // actually block their own address from here. Hold the pending
  // duration + which guard tripped, and render Block anyway / Cancel.
  const [confirmGuard, setConfirmGuard] = useState<
    { readonly kind: 'self' | 'shared'; readonly duration: ManualBlockDuration } | null
  >(null);
  // Suspicious v2 §3.3 — mobile sticky bottom bar / More sheet.
  // Hide the inline BlockControl on mobile; the sticky bar takes over.
  const isMobile = useIsMobile();
  // The status line counts the SITE's live blocks, not just this sender's
  // (founder 2026-10-19: "No addresses are blocked yet" beside BLOCKED · 4).
  const [siteLiveBlocks, setSiteLiveBlocks] = useState<number | null>(null);
  const thisBlockId = data.actions.block?.block_id ?? null;
  useEffect(() => {
    if (http === null) return;
    let live = true;
    void getBlocking(http, siteId)
      .then((r) => { if (live && typeof r?.counts?.live_blocks === 'number') setSiteLiveBlocks(r.counts.live_blocks); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [http, siteId, thisBlockId]);

  const onShare = useCallback(() => {
    try {
      const url = new URL(window.location.origin + window.location.pathname);
      url.searchParams.set('view', 'mandates');
      url.searchParams.set('segment', 'suspicious');
      url.searchParams.set('site', siteId);
      // Deep-link to the newest alert id of the group — API resolves it.
      url.searchParams.set('threat', data.sender.sender_ref);
      void navigator.clipboard.writeText(url.toString());
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  }, [siteId, data.sender.sender_ref]);

  const onBlockAction = useCallback(async (outcome: BlockControlOutcome): Promise<void> => {
    if (http === null) { setError('The dashboard is offline.'); return; }
    setPending(true);
    setError(null);
    try {
      if (outcome.action === 'block') {
        const body: {
          target: { kind: 'ip'; value: string };
          duration: ManualBlockDuration;
          source?: { kind: 'threat'; ref: string };
          confirm_self?: true;
          confirm_shared?: true;
        } = {
          target: { kind: 'ip', value: data.who.sender_ip_display },
          duration: outcome.duration,
          source: { kind: 'threat', ref: data.sender.sender_ref },
        };
        if (outcome.confirm_self === true) body.confirm_self = true;
        if (outcome.confirm_shared === true) body.confirm_shared = true;
        const res = await postManualBlock(http, siteId, body);
        if (!res.ok) {
          if (res.error === 'self_block_confirm_required'
            || res.error === 'shared_network_confirm_required') {
            // Founder bug 2026-10-20 (coordinator item 8): open the
            // in-panel confirm with Block anyway / Cancel instead of
            // a dead-end error line.
            setConfirmGuard({
              kind: res.error === 'self_block_confirm_required' ? 'self' : 'shared',
              duration: outcome.duration,
            });
            return;
          } else if (res.error === 'verified_crawler') {
            setError('That address overlaps a verified crawler and was not blocked.');
          } else if (res.error === 'cdn_or_proxy_range') {
            setError("That address belongs to Cloudflare or a privacy relay used by many real visitors, so Sill won't block it.");
          } else if (res.error === 'cloud_ranges_loading') {
            setError('Sill is still loading network data. Try again in a few seconds.');
          } else if (res.error === 'blocklist_cap_reached') {
            setError('You have reached the per-site blocklist cap. Remove an older block first.');
          } else if (res.error === 'cidr_too_broad') {
            setError('That range is too broad. Use /24 or narrower for IPv4, /48 for IPv6.');
          } else if (res.error === 'plan_feature_required') {
            // ADR-0263 S4 — plain-copy message with the plan name.
            setError(res.plan_message ?? (res.plan_name !== null && res.plan_name !== undefined
              ? `Blocking is part of the ${res.plan_name} plan.`
              : "Blocking is not on this site's plan."));
          } else {
            setError('That action was rejected.');
          }
          return;
        }
        onActionsChange(res.state);
      } else if (outcome.action === 'ignore') {
        // Suspicious v2 §5.5 — sender-scoped Ignore (every class the
        // sender raised; 30-day suppression). Falls back to the
        // per-class legacy if the sender_ref is somehow missing.
        const res = await postSenderIgnore(http, siteId, data.sender.sender_ref);
        if (!res.ok) {
          setError(res.error === 'rate_limited'
            ? 'Too many actions in the last minute. Wait a moment and try again.'
            : res.error === 'forbidden'
              ? 'Only an owner or admin can ignore a sender.'
              : 'That ignore was rejected.');
          return;
        }
        onActionsChange({ ...data.actions, ignored_at: res.expires_at });
      } else {
        // Trust uses the never_block threat action. Founder bug
        // 2026-10-18: if the address is already blocked, DELETE the
        // block first so Trust is a single mental step for the
        // merchant (confirm copy says both actions happen).
        const alert_id = data.alerts[0]?.threat_alert_id;
        if (alert_id === undefined) { setError('No alert to trust.'); return; }
        if (data.actions.block !== null) {
          try {
            await http.request({
              method: 'DELETE',
              path: `/v1/sites/${encodeURIComponent(siteId)}/blocklist/${encodeURIComponent(data.actions.block.block_id)}`,
            });
          } catch {
            // Stop here: trusting while the block stays would leave both.
            setError('Could not remove the block. Nothing was changed. Try again.');
            return;
          }
        }
        await http.request({
          method: 'POST',
          path: `/v1/sites/${encodeURIComponent(siteId)}/threats/${encodeURIComponent(alert_id)}/actions`,
          body: { action: 'never_block' },
        });
        onActionsChange({
          ...data.actions,
          block: null,
          allow: {
            allow_id: '',
            target_kind: 'ip',
            target_display: data.who.sender_ip_display,
            created_at: new Date().toISOString(),
          },
        });
      }
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setPending(false);
    }
  }, [data, http, onActionsChange, siteId]);

  const hasExistingBlock = data.actions.block !== null;
  const trustBody = hasExistingBlock
    ? `Trust ${data.who.sender_ip_display}? This removes the block and Sill won't alert on or block this address.`
    : `Trust ${data.who.sender_ip_display}? Sill won't alert on or block this address.`;

  // Founder bug 2026-10-18 (block-state consistency): when the sender
  // is already blocked, swap Block ▾ for a "Blocked until …" chip +
  // Unblock button on the DetailActionRow. Trust is still accessible
  // in the ⋯ menu (and unblocks first via the Trust confirm).
  const blockedChipProps = hasExistingBlock && data.actions.block !== null ? {
    blockedUntilLabel: senderChipLabel(data.actions.block, data.actions.blocking),
    blockedTone: (data.actions.blocking.mode === 'off' ? 'amber' : 'coral') as 'coral' | 'amber',
    onUnblock: async () => {
      if (http === null || data.actions.block === null) return;
      try {
        await http.request({
          method: 'DELETE',
          path: `/v1/sites/${encodeURIComponent(siteId)}/blocklist/${encodeURIComponent(data.actions.block.block_id)}`,
        });
      } catch { /* non-fatal — the next refresh will reconcile */ }
      onActionsChange({ ...data.actions, block: null });
    },
  } : {};

  // Build the action-row's handlers. All four (block / trust / ignore
  // / share) run through the same `onBlockAction` chain used by the
  // old inline BlockControl so retry / error state is identical.
  // Founder bug 2026-10-18: Trust opens a confirm first so the
  // merchant knows the block is also being removed.
  const actionProps = {
    onBlock: (duration: ActionDuration) => void onBlockAction({ action: 'block', duration }),
    onTrust: () => setTrustConfirming(true),
    onIgnore: () => void onBlockAction({ action: 'ignore' }),
    onShare,
    shareJustCopied: shareCopied,
    blockPending: pending,
    blockDisabledReason: blockUnavailableReason(data.actions.blocking, { shopify: isShopify }),
    ...blockedChipProps,
  } as const;
  const stateBanner = renderStateBanner(data.actions, data.who.sender_ip_display, tz, C, siteDomain);
  // Founder bug 2026-10-18: when a block covers the sender, keep the
  // DetailActionRow visible so the chip + Unblock + ⋯ Trust remain
  // one-click. The state banner stays for extra context when it adds
  // delivery nuance (block saved · not active, etc.).
  const showActions = data.actions.may_mutate && (stateBanner === null || data.actions.block !== null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <HeaderRow
        sev={sev}
        country={data.who.country}
        region={data.who.region}
        ip={data.who.sender_ip_display}
        titleParts={titleParts}
        requests_display={data.what.requests_display}
        first_seen={data.sender.first_seen}
        last_seen={data.sender.last_seen}
        actionsRight={!isMobile && showActions ? <DetailActionRow {...actionProps} /> : null}
      />
      {/* Status line directly under the action row, on every panel. */}
      {showActions ? (
        <TurnOnBlockingLink
          siteId={siteId}
          siteDomain={siteDomain}
          http={http}
          blocking={data.actions.blocking}
          liveBlocksCount={siteLiveBlocks ?? (data.actions.block !== null ? 1 : 0)}
          canMutate={data.actions.may_mutate}
          onModeFlipped={(install_override) => {
            onActionsChange({
              ...data.actions,
              blocking: install_override === null
                ? { ...data.actions.blocking, mode: 'on' }
                : { ...data.actions.blocking, install: install_override },
            });
          }}
        />
      ) : null}
      {data.what.any_success_200 ? (
        <div
          data-testid="sender-urgent-banner"
          style={{
            fontFamily: sansFont, fontSize: 13, color: C.coralFg,
            padding: '10px 12px',
            border: `1px solid ${C.coralFg}`,
            borderRadius: 2,
          }}
        >
          One request got a 200 on a sensitive path &mdash; check whether that was the real file or your normal not-found page.
        </div>
      ) : null}
      {stateBanner}
      {!data.actions.may_mutate && stateBanner === null ? (
        <div data-testid="sender-reviewer-note" style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
          Only owners and admins can block.
        </div>
      ) : null}
      {error !== null ? (
        <div data-testid="sender-action-error" style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg }}>
          {error}
        </div>
      ) : null}
      {/* Founder bug 2026-10-20 (coordinator item 8): the server
          returned a self-block / shared-network guard. Render the same
          Block anyway / Cancel confirm BlockControl uses on the inline
          popover, inside the detail panel. */}
      {confirmGuard !== null ? (
        <div
          data-testid="sender-confirm-guard"
          data-kind={confirmGuard.kind}
          style={{
            padding: '10px 12px',
            border: `1px solid ${C.amberFg}`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
          }}
        >
          <div style={{ marginBottom: 8, overflowWrap: 'anywhere' }}>
            {confirmGuard.kind === 'self'
              ? `This is your current address. When blocking is on for ${siteDomain ?? 'this site'}, you won't be able to open it from this network. The Sill dashboard and your other sites keep working.`
              : `${data.who.sender_ip_display} is on a home or mobile network and may be shared by other people. Blocking it forever will stop everyone on that network from reaching ${siteDomain ?? 'this site'}.`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              data-testid="sender-confirm-guard-block"
              disabled={pending}
              onClick={() => {
                const g = confirmGuard;
                setConfirmGuard(null);
                void onBlockAction(g.kind === 'self'
                  ? { action: 'block', duration: g.duration, confirm_self: true }
                  : { action: 'block', duration: g.duration, confirm_shared: true });
              }}
              style={{
                fontFamily: sansFont, fontSize: 13,
                color: C.ink, background: C.lime,
                border: `1px solid ${C.lime}`, borderRadius: 2,
                padding: '6px 10px',
                cursor: pending ? 'wait' : 'pointer',
              }}
            >
              {confirmGuard.kind === 'self' ? 'Block anyway' : 'Block forever anyway'}
            </button>
            <button
              type="button"
              data-testid="sender-confirm-guard-cancel"
              disabled={pending}
              onClick={() => setConfirmGuard(null)}
              style={{
                fontFamily: sansFont, fontSize: 13,
                color: C.text, background: 'transparent',
                border: `1px solid ${C.border}`, borderRadius: 2,
                padding: '6px 10px',
                cursor: pending ? 'wait' : 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      {data.ai_summary !== null ? (
        <AiSummaryBlock summary={data.ai_summary} />
      ) : null}
      <WhoBlock data={data} />
      <WhatBlock data={data} tz={tz} />
      <WhyBlock data={data} />
      <HistoryBlock data={data} tz={tz} />
      {/* Mobile: the same DetailActionRow sticky at the bottom. */}
      {isMobile && showActions ? (
        <div
          data-testid="sender-mobile-sticky-bar"
          style={{
            position: 'sticky',
            bottom: 0,
            marginTop: 16,
            padding: '10px 12px',
            background: C.surface,
            borderTop: `1px solid ${C.border}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            zIndex: 2,
          }}
        >
          <DetailActionRow {...actionProps} menuPlacement="up" />
        </div>
      ) : null}
      <ConfirmDialog
        open={trustConfirming && !pending}
        onClose={() => { if (!pending) setTrustConfirming(false); }}
        title="Trust this address?"
        body={trustBody}
        confirmLabel={hasExistingBlock ? 'Unblock and trust' : 'Trust'}
        confirmTone="lime"
        onConfirm={() => {
          setTrustConfirming(false);
          void onBlockAction({ action: 'trust' });
        }}
      />
    </div>
  );
}

function HeaderRow(props: {
  readonly sev: ReturnType<typeof severityIcon>;
  readonly country: string | null;
  readonly region: string | null;
  /** Raw IP for owner/admin/reviewer, masked (`1.2.3.x`) otherwise.
   *  Kept on the props for callers that still want it; the title row
   *  no longer renders it (founder bug 2026-10-20 Report 7 — the WHO
   *  block below shows it one row down, click-to-copy). */
  readonly ip: string;
  readonly titleParts: string;
  readonly requests_display: string;
  readonly first_seen: string;
  readonly last_seen: string;
  readonly actionsRight: ReactNode;
}): JSX.Element {
  const C = useC();
  // Founder bug 2026-10-20 (Report 7): long IPv6 ran the title way off
  // the right side ("🇺🇸 Mt. Pleasant, United States · 2601:742:…").
  // The IP is already in WHO (one scroll down, click-to-copy); keep
  // the title to shield + flag + place only. `props.ip` is retained
  // on the signature so existing callers compile; the render drops it.
  void props.ip;
  const country = countryName(props.country) ?? props.country ?? null;
  const region = typeof props.region === 'string' && props.region.length > 0 ? props.region : null;
  const location = country === null && region === null
    ? 'Unknown location'
    : region === null ? country!
      : country === null ? region
        : `${region}, ${country}`;
  return (
    <div
      data-testid="sender-header"
      style={{ minWidth: 0 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <Tooltip content={props.sev.tip}>
          <span aria-label={props.sev.tip} style={{ display: 'inline-flex', flexShrink: 0 }}>
            {props.sev.node as ReactNode}
          </span>
        </Tooltip>
        {props.country !== null ? <CountryFlag code={props.country} size={16} /> : null}
        <span
          data-testid="sender-header-location"
          style={{ fontFamily: sansFont, fontSize: 13, color: C.body, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {location}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ marginLeft: 'auto', flexShrink: 0 }}>{props.actionsRight}</span>
      </div>
      <div
        data-testid="sender-header-title"
        style={{
          fontFamily: serifFont,
          fontSize: 22,
          lineHeight: 1.2,
          color: C.text,
          marginTop: 6,
          overflowWrap: 'anywhere',
        }}
      >
        {props.titleParts}
      </div>
      <div
        data-testid="sender-header-subline"
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          marginTop: 4,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <span>{props.requests_display}</span>
        <span>&middot;</span>
        <span>
          first seen <RelativeTime iso={props.first_seen} />
        </span>
        <span>&middot;</span>
        <span>
          last <RelativeTime iso={props.last_seen} />
        </span>
      </div>
    </div>
  );
}

function renderStateBanner(
  actions: BlockState,
  _target_display: string,
  tz: string,
  C: ReturnType<typeof useC>,
  siteDomain: string | null,
): JSX.Element | null {
  // Founder report 2026-10-20 item 2: the right panel's "top section"
  // (state banner + its title + body) no longer prints the raw IP.
  // The WHO › Address row remains the canonical place for it, with
  // click-to-copy. The banners read as "Trusted", "Blocked forever",
  // "Access denied until …" — never "Trusted · 203.0.113.42".
  void _target_display;
  if (actions.allow !== null) {
    return (
      <StateBanner
        testId="sender-trusted-state"
        tone="mint"
        kicker="TRUSTED"
        title="Trusted"
        body="Sill won't alert on or block this address."
        C={C}
      />
    );
  }
  if (actions.block !== null) {
    const block = actions.block;
    const until = block.expires_at === null
      ? 'forever'
      : formatBlockUntil(block.expires_at, tz);
    if (block.delivered) {
      return (
        <StateBanner
          testId="sender-blocked-state"
          tone="coral"
          kicker="BLOCKED"
          title={block.expires_at === null
            ? 'Blocked forever'
            : `Access denied until ${until}`}
          body={`Requests from this address get “Access denied.” Sill has seen ${block.would_block_since_count} attempt${block.would_block_since_count === 1 ? '' : 's'} from it since.`}
          C={C}
        />
      );
    }
    if (actions.blocking.mode === 'off') {
      return (
        <StateBanner
          testId="sender-block-saved-state"
          tone="amber"
          kicker="BLOCK SAVED · NOT ACTIVE"
          title={`Saved until ${until}`}
          body={`Blocking is off for ${siteDomain ?? 'this site'}, so these requests still get through. ${block.would_block_since_count} would have been stopped.`}
          C={C}
        />
      );
    }
    if (actions.blocking.install === 'not_in_path') {
      return (
        <StateBanner
          testId="sender-block-not-in-path-state"
          tone="amber"
          kicker="BLOCK SAVED · NOT ACTIVE"
          title={`Saved until ${until}`}
          body="Sill can't stop these on this install. Block this address in your Cloudflare or hosting firewall."
          C={C}
        />
      );
    }
    // mode on + install ok but delivery not live → starting
    return (
      <StateBanner
        testId="sender-block-starting-state"
        tone="amber"
        kicker="BLOCKED · STARTING"
        title={`Saved until ${until}`}
        body={actions.blocking.delivery === 'pending'
          ? 'Your Worker picks up changes within a minute.'
          : "Sill can't deliver your block list right now. Blocks are saved and apply once delivery recovers."}
        C={C}
      />
    );
  }
  if (actions.ignored_at !== null) {
    return (
      <StateBanner
        testId="sender-ignored-state"
        tone="muted"
        kicker="IGNORED"
        title="Ignored"
        body="Sill won't alert on these attempts from this address for 30 days. You can still block it."
        C={C}
      />
    );
  }
  return null;
}

function StateBanner({
  testId, tone, kicker, title, body, C,
}: {
  readonly testId: string;
  readonly tone: 'coral' | 'amber' | 'mint' | 'muted';
  readonly kicker: string;
  readonly title: string;
  readonly body: string;
  readonly C: ReturnType<typeof useC>;
}): JSX.Element {
  const colour = tone === 'amber' ? C.amberFg
    : tone === 'mint' ? C.mintFg
      : tone === 'coral' ? C.coralFg
        : C.muted;
  return (
    <div
      data-testid={testId}
      style={{
        padding: '10px 12px',
        border: `1px solid ${colour}`,
        borderRadius: 2,
        fontFamily: sansFont, fontSize: 13, color: C.text,
      }}
    >
      <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: colour, marginBottom: 4 }}>
        {kicker}
      </div>
      <div style={{ fontWeight: 600, marginBottom: 4, overflowWrap: 'anywhere' }}>{title}</div>
      <div style={{ color: C.muted, overflowWrap: 'anywhere' }}>{body}</div>
    </div>
  );
}

function buildTitleParts(labels: readonly string[]): string {
  if (labels.length === 0) return 'A suspicious visitor';
  const first = labels[0]!;
  if (labels.length === 1) return first;
  const second = labels[1]!;
  const lower = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);
  if (labels.length === 2) return `${first} and ${lower(second)}`;
  return `${first}, ${lower(second)} and ${labels.length - 2} more`;
}

function AiSummaryBlock({ summary }: { readonly summary: { readonly sentence: string; readonly verdict: 'safe' | 'monitor' | 'block' } }): JSX.Element {
  const C = useC();
  const label = summary.verdict === 'safe' ? 'Looks safe'
    : summary.verdict === 'monitor' ? 'Suggests watching'
    : 'Suggests blocking';
  const chipColour = summary.verdict === 'safe' ? C.limeFg
    : summary.verdict === 'monitor' ? C.amberFg
    : C.coralFg;
  return (
    <section data-testid="sender-ai-summary">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <SectionKicker>AI SUMMARY</SectionKicker>
        <span
          data-testid="sender-ai-chip"
          style={{
            marginLeft: 'auto',
            fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5,
            padding: '2px 6px',
            border: `1px solid ${chipColour}`,
            color: chipColour,
            borderRadius: 2,
          }}
        >
          {label}
        </span>
      </div>
      <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, marginTop: 6, overflowWrap: 'anywhere' }}>
        {summary.sentence}
      </div>
      <div
        data-testid="sender-ai-footnote"
        style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 0.3, color: C.muted, marginTop: 4 }}
      >
        Written by AI from the requests below. Advice only.
      </div>
    </section>
  );
}

function WhoBlock({ data }: { readonly data: ThreatSenderDetailResponse }): JSX.Element {
  const C = useC();
  const w = data.who;
  const hidden = w.ip_status === 'hidden';
  const country = countryName(w.country) ?? w.country ?? null;
  const region = typeof w.region === 'string' && w.region.length > 0 ? w.region : null;
  const location = country === null && region === null
    ? null
    : region === null ? country : country === null ? region : `${region}, ${country}`;
  const network = w.network_type === 'hosting' ? 'Datacenter / hosting network'
    : w.network_type === 'residential' ? 'Residential or mobile network'
      : null;
  const ua = w.says_it_is.length > 0 ? w.says_it_is : null;
  return (
    <section data-testid="sender-who">
      <SectionKicker>WHO</SectionKicker>
      <Row label="Address">
        <Mono>{w.sender_ip_display}</Mono>
        {hidden ? (
          <span style={{ marginLeft: 8, fontFamily: monoFont, fontSize: 10, color: C.muted }}>
            Only owners, admins and reviewers see the full address.
          </span>
        ) : null}
      </Row>
      {location !== null ? <Row label="Location">{location}</Row> : null}
      {network !== null || w.asn !== null ? (
        <Row label="Network">
          {network}
          {w.asn !== null ? (
            <>
              {network !== null ? ' ' : ''}
              <Mono color={C.muted}>{network !== null ? '· ' : ''}AS{w.asn}</Mono>
              {w.as_org !== null ? <Mono color={C.muted}>{` ${w.as_org}`}</Mono> : null}
            </>
          ) : null}
        </Row>
      ) : null}
      {ua !== null ? (
        <Row label="Says it is">
          <span
            // The full UA is hidden by the 2-line clamp — title shows it on hover (title-ok: truncated value)
            title={ua}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontFamily: monoFont,
              fontSize: 12,
              color: C.text,
              wordBreak: 'break-all',
            }}
          >
            {ua}
          </span>
        </Row>
      ) : null}
      {w.claimed_agent_id !== null ? (
        <Row label="Identity">
          <span style={{ color: w.identity_verified === false ? C.coralFg : C.text }}>
            Claimed to be {w.claimed_agent_id} &mdash; {
              w.identity_verified === true ? 'verification passed'
                : w.identity_verified === false ? 'verification failed'
                : 'not verified'}
          </span>
        </Row>
      ) : null}
    </section>
  );
}

/** Founder bug 2026-10-20 Report 1: the collapsed per-sender list used
 *  to be a single "Show all N" toggle that then rendered every request
 *  as a single wall of day groups. Long-running attackers were
 *  thousands of rows and ran off the viewport. Now the collapsed
 *  default stays at 10 (newest first) with a "Show all N" control; the
 *  expanded view paginates 25/page using the same Pager pattern as
 *  AddressTables. */
const REQUESTS_COLLAPSED = 10;
const REQUESTS_PAGE_SIZE = 25;

function WhatBlock({ data, tz }: {
  readonly data: ThreatSenderDetailResponse;
  readonly tz: string;
}): JSX.Element {
  const C = useC();
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(0);
  const [copied, setCopied] = useState(false);
  const requests = data.what.requests;
  const pages = Math.max(1, Math.ceil(requests.length / REQUESTS_PAGE_SIZE));
  const start = Math.min(page, pages - 1) * REQUESTS_PAGE_SIZE;
  const visible = expanded
    ? requests.slice(start, start + REQUESTS_PAGE_SIZE)
    : requests.slice(0, REQUESTS_COLLAPSED);
  const summary = data.what.summary_line;
  // Plain-language lines next to each class label the server returned.
  // Rendered under the summary as a disclosure — never a `title`
  // attribute (existing lint test forbids explanatory titles).
  const plainLines = (data.sender.class_labels ?? [])
    .map((label) => ({ label, line: plainClassLine(label) }))
    .filter((x) => typeof x.line === 'string') as ReadonlyArray<{ label: string; line: string }>;

  const copyForHost = (): void => {
    const lines: string[] = [];
    lines.push(`Address: ${data.who.sender_ip_display}`);
    const city = typeof data.who.region === 'string' && data.who.region.length > 0 ? data.who.region : null;
    const country = typeof data.who.country === 'string' && data.who.country.length > 0 ? data.who.country : null;
    const where = city !== null && country !== null ? `${city}, ${country}` : (country ?? city);
    if (where !== null) lines.push(`Location: ${where}`);
    const network = data.who.as_org ?? (data.who.asn !== null ? `AS${data.who.asn}` : null);
    if (network !== null) lines.push(`Network: ${network}`);
    lines.push(`First seen: ${data.sender.first_seen}`);
    lines.push(`Last seen: ${data.sender.last_seen}`);
    lines.push('');
    lines.push('Requests:');
    for (const r of requests) {
      const method = r.method ?? '-';
      const status = r.origin_status !== null ? String(r.origin_status) : '-';
      const query = r.query_display !== null && r.query_display.length > 0 ? `?${r.query_display}` : '';
      lines.push(`${r.at}\t${method}\t${r.path_display}${query}\t${status}`);
    }
    const text = lines.join('\n');
    const done = (): void => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    try {
      void navigator.clipboard.writeText(text).then(done, () => {
        // Fallback: insert a hidden textarea and execCommand('copy').
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.setAttribute('readonly', '');
          ta.style.position = 'absolute';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          done();
        } catch { /* clipboard blocked */ }
      });
    } catch {
      // Older browsers w/o navigator.clipboard.
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        done();
      } catch { /* clipboard blocked */ }
    }
  };

  return (
    <section data-testid="sender-what">
      <SectionKicker>WHAT THEY TRIED</SectionKicker>
      {summary.length > 0 ? (
        <div data-testid="sender-what-summary" style={{ fontFamily: sansFont, fontSize: 13, color: C.body, marginTop: 2, overflowWrap: 'anywhere' }}>
          {summary}
        </div>
      ) : null}
      {plainLines.length > 0 ? (
        <details
          data-testid="sender-what-plain"
          style={{ marginTop: 4, fontFamily: sansFont, fontSize: 12, color: C.muted }}
        >
          <summary style={{ cursor: 'pointer', listStyle: 'none' }}>
            What those mean &rarr;
          </summary>
          <ul style={{ listStyle: 'none', margin: '4px 0 0 0', padding: 0 }}>
            {plainLines.map((p) => (
              <li
                key={p.label}
                data-testid={`sender-what-plain-${p.label}`}
                style={{ padding: '2px 0', overflowWrap: 'anywhere' }}
              >
                <span style={{ color: C.body }}>{p.label}:</span> {p.line}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {visible.length === 0 ? (
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 6 }}>
          No signal rows survived retention for this sender.
        </div>
      ) : (
        <ul data-testid="sender-request-list" style={{ listStyle: 'none', margin: '6px 0 0 0', padding: 0 }}>
          {renderDayGroups(visible, tz, C)}
        </ul>
      )}
      {expanded && requests.length > REQUESTS_PAGE_SIZE ? (
        <SenderRequestsPager
          page={Math.min(page, pages - 1)}
          pages={pages}
          start={start}
          shown={visible.length}
          total={requests.length}
          onPage={setPage}
          C={C}
        />
      ) : null}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
        {!expanded && requests.length > REQUESTS_COLLAPSED ? (
          <button
            type="button"
            onClick={() => { setExpanded(true); setPage(0); }}
            data-testid="sender-show-all"
            style={{
              fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5,
              color: C.limeFg,
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            }}
          >
            Show all {requests.length} requests
          </button>
        ) : null}
        {/* Copy for my host — plain-text block the merchant can paste
            into a hosting support ticket. Visible to anyone who can
            already see the sender detail. */}
        {data.who.sender_ip_display.length > 0 ? (
          <button
            type="button"
            onClick={copyForHost}
            data-testid="sender-copy-for-host"
            style={{
              fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5,
              color: C.limeFg,
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            }}
          >
            {copied ? 'Copied' : 'Copy for my host'}
          </button>
        ) : null}
      </div>
    </section>
  );
}

/** Mirrors the Pager in `settings/AddressTables.tsx`; small enough to
 *  live here. 25/page previous · page · next. Only rendered when the
 *  requests list is expanded and larger than one page. */
function SenderRequestsPager({ page, pages, start, shown, total, onPage, C }: {
  readonly page: number;
  readonly pages: number;
  readonly start: number;
  readonly shown: number;
  readonly total: number;
  readonly onPage: (p: number) => void;
  readonly C: ReturnType<typeof useC>;
}): JSX.Element {
  const btn = (disabled: boolean): React.CSSProperties => ({
    fontFamily: sansFont, fontSize: 12, padding: '5px 10px', minHeight: 32,
    background: 'transparent', color: disabled ? C.muted : C.text,
    border: `1px solid ${C.border}`, borderRadius: 2,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
  });
  return (
    <div
      data-testid="sender-requests-pager"
      style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}
    >
      <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginRight: 'auto' }}>
        {start + 1}&ndash;{start + shown} of {total.toLocaleString('en-US')}
      </span>
      <button
        type="button"
        data-testid="sender-requests-pager-prev"
        disabled={page === 0}
        onClick={() => onPage(page - 1)}
        style={btn(page === 0)}
      >
        Previous
      </button>
      <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
        Page {page + 1} of {pages}
      </span>
      <button
        type="button"
        data-testid="sender-requests-pager-next"
        disabled={page >= pages - 1}
        onClick={() => onPage(page + 1)}
        style={btn(page >= pages - 1)}
      >
        Next
      </button>
    </div>
  );
}

function renderDayGroups(
  requests: ReadonlyArray<ThreatSenderDetailResponse['what']['requests'][number]>,
  tz: string,
  C: ReturnType<typeof useC>,
): JSX.Element[] {
  const groups = new Map<string, ThreatSenderDetailResponse['what']['requests'][number][]>();
  const now = new Date();
  for (const r of requests) {
    const d = new Date(r.at);
    const key = Number.isNaN(d.getTime()) ? 'unknown' : dayLabel(d, tz, now);
    const arr = groups.get(key) ?? [];
    arr.push(r);
    groups.set(key, arr);
  }
  const out: JSX.Element[] = [];
  for (const [day, items] of groups) {
    out.push(
      <li key={`day-${day}`} data-testid="sender-day" style={{ padding: '6px 0' }}>
        <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, marginBottom: 4 }}>{day}</div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map((r, i) => (
            <li
              key={`${r.at}-${i}`}
              data-testid="sender-request"
              style={{
                padding: '6px 0',
                borderBottom: i < items.length - 1 ? `1px solid ${C.borderSoft}` : 'none',
                display: 'grid',
                gridTemplateColumns: '88px 44px 1fr 40px',
                gap: 12,
                alignItems: 'baseline',
              }}
            >
              <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
                {formatClock(r.at, tz)}
              </span>
              <span style={{ fontFamily: monoFont, fontSize: 11, color: C.text }}>
                {r.method ?? '—'}
              </span>
              <span data-testid="sender-request-path" style={{ fontFamily: monoFont, fontSize: 11, color: C.text, overflowWrap: 'anywhere' }}>
                {r.path_display}
                {r.query_display !== null && r.query_display.length > 0 ? (
                  <>
                    <span style={{ color: C.muted }}>?</span>
                    <span style={{ color: C.muted }}>{r.query_display}</span>
                  </>
                ) : null}
              </span>
              <span style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: r.origin_status !== null && r.origin_status >= 200 && r.origin_status < 300 ? C.coralFg :
                       r.origin_status !== null && r.origin_status >= 400 ? C.amberFg : C.muted,
                textAlign: 'right',
              }}>
                {r.origin_status !== null ? r.origin_status : '—'}
              </span>
            </li>
          ))}
        </ul>
      </li>,
    );
  }
  return out;
}

function WhyBlock({ data }: { readonly data: ThreatSenderDetailResponse }): JSX.Element {
  const C = useC();
  const reasons = data.confidence.reasons.filter((r) => !/other sites? using Sill/i.test(r));
  return (
    <section data-testid="sender-why">
      <SectionKicker>{`WHY IT'S FLAGGED · ${data.confidence.word.toUpperCase()}`}</SectionKicker>
      {reasons.length > 0 ? (
        <ul data-testid="sender-reasons" style={{ listStyle: 'none', margin: '4px 0 0 0', padding: 0 }}>
          {reasons.map((r, i) => (
            <li
              key={`${i}-${r}`}
              style={{ padding: '2px 0', fontFamily: sansFont, fontSize: 12, color: C.text, overflowWrap: 'anywhere' }}
            >
              &middot; {r}
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>No reasons recorded.</div>
      )}
    </section>
  );
}

function HistoryBlock({ data, tz }: { readonly data: ThreatSenderDetailResponse; readonly tz: string }): JSX.Element {
  const C = useC();
  const h = data.history;
  return (
    <section data-testid="sender-history">
      <SectionKicker>HISTORY</SectionKicker>
      <Row label="On this site">
        <span>{h.sentence}</span>
        {h.first_seen_on_site !== null ? (
          <span style={{ color: C.muted, marginLeft: 6 }}>
            Since {formatShortDate(h.first_seen_on_site, tz)}.
          </span>
        ) : null}
        {h.prior_alerts_30d > 0 ? (
          <span style={{ color: C.amberFg, marginLeft: 6 }}>
            {h.prior_alerts_30d.toLocaleString('en-US')} prior alert{h.prior_alerts_30d === 1 ? '' : 's'} in 30 days.
          </span>
        ) : null}
      </Row>
      <Row label="Before this">
        {h.pages_before_probing.length === 0 ? (
          <span style={{ color: C.muted }}>None</span>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {h.pages_before_probing.map((p, i) => (
              <li key={`${p.at}-${i}`} style={{ padding: '2px 0', display: 'flex', gap: 8 }}>
                <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, minWidth: 76 }}>
                  {formatShortDate(p.at, tz)}
                </span>
                <span style={{ fontFamily: monoFont, fontSize: 11, color: C.text, overflowWrap: 'anywhere' }}>
                  {p.path_display}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Row>
      <Row label="Other sites">{data.elsewhere.summary}</Row>
    </section>
  );
}

function SectionKicker({ children }: { readonly children: string }): JSX.Element {
  const C = useC();
  return (
    <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: C.muted, marginBottom: 6 }}>
      {children}
    </div>
  );
}

function Row({ label, children }: { readonly label: string; readonly children: ReactNode }): JSX.Element {
  const C = useC();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(90px, 110px) 1fr', gap: 12, padding: '4px 0' }}>
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1 }}>{label}</div>
      <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, overflowWrap: 'anywhere', minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}

function Mono({ children, color }: { readonly children: ReactNode; readonly color?: string }): JSX.Element {
  const C = useC();
  return <span style={{ fontFamily: monoFont, fontSize: 12, color: color ?? C.text }}>{children}</span>;
}

function formatBlockUntil(iso: string, tz: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
  } catch { return iso; }
}

function formatShortDate(iso: string, tz: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric' }).format(d);
  } catch { return iso; }
}

function dayLabel(d: Date, tz: string, now: Date): string {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
    const td = fmt.format(d);
    const tn = fmt.format(now);
    if (td === tn) return 'TODAY';
    const yn = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    if (td === fmt.format(yn)) return 'YESTERDAY';
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric', year: 'numeric' }).format(d).toUpperCase();
  } catch { return ''; }
}

function formatClock(iso: string, tz: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
  } catch { return iso; }
}

// Suppress unused warning when BlockingState is passed indirectly.
export type _BlockingStateExport = BlockingState;
